import {
  Button,
  Col,
  ConfigProvider,
  ConfirmModal,
  Empty,
  Flex,
  Row,
  Space,
  Table,
  type TableRef,
  Text,
  ToggleAllButton,
  useExpandedRowKeysContext,
  useTableRowSelection,
} from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { PinpointMarkerForTableCell } from '@/features/comment/components/pinpoint/PinpointMarkerForTableCell';
import { pinpointTableActionColumn } from '@/features/comment/components/pinpoint/PinpointTableRowMenuCell';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { useExpenditureBudgetPurpose } from '@/features/master-table/hooks';
import {
  AccountingTypeLabelModifier,
  accountingType,
} from '@/features/master-table/models';
import { splitExpenditureCartsByAccountingType } from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import { PREVIEW_ROW_ID } from '@/features/project/_utils/constants';
import type {
  ExpenditureCartModel,
  ExpenditureCartsModel,
} from '@/features/project/projects/_models/expenditure-cart';
import type { ExpenditureItemModel } from '@/features/project/projects/_models/expenditure-item';
import { useDeleteExpenditureItem } from '@/features/project/projects/detail/income-and-expenditure/_hooks/expenditure-item';
import { useId } from '@/hooks';
import { useLocaledAmount, useMessage } from '@/providers';
import { formatValueByUnit } from '@/utils';
import classNames from 'classnames';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { ExpenditureItemsTable } from '../expenditure-items/ExpenditureItemsTable';
import { usePreviewRowState } from '../shared/PreviewRowStateProvider';
import styles from './ExpenditureCartsTable.module.scss';

type Props = {
  data: z.infer<typeof ExpenditureCartsModel>;
  projectId: string;
  subProjectId: string;
  onClickDelete: (value: z.infer<typeof ExpenditureCartModel>) => void;
  editable: boolean;
};

const tableStyles = classNames(
  styles.ant_table_content,
  styles.ant_table_container,
  styles.ant_row,
);
const tableHeaderStyle = {
  fontSize: 12,
};

const PREVIEW_ROW: z.infer<typeof ExpenditureCartModel> = {
  id: PREVIEW_ROW_ID,
  createdAt: '',
  updatedAt: '',
  priorId: null,
  subProjectId: '',
  accountingId: '',
  accountTitleId: null,
  reasonForChange: null,
  remark: null,
  desiredAmount: null,
  accountingType: {
    id: '',
    code: 1,
    type: accountingType.options[0],
    name: '',
    startYear: null,
    endYear: null,
    createdAt: '',
    updatedAt: '',
  },
  accountTitle: null,
  expenditureItems: [],
  executionItem: null,
};

export const RsExpenditureCartsTable: FC<Props> = ({
  data,
  projectId,
  subProjectId,
  onClickDelete,
  editable,
}) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const tableRef = useRef<TableRef>(null);
  const { id } = useId();
  const { unit } = useLocaledAmount();
  const { setDrawerState } = useProjectDetailDrawerState();
  const [expenditureItemDelete, setExpenditureItemDelete] = useState<z.infer<
    typeof ExpenditureItemModel
  > | null>(null);
  const { mutateAsync: deleteExpenditureItemAsync } =
    useDeleteExpenditureItem();
  const {
    expandedRowKeys,
    addExpandedRowKeys,
    removeExpandedRowKeys,
    onExpand,
  } = useExpandedRowKeysContext();
  const project = useProject(id);
  const editPermission = useProjectPermission(true);
  const { data: majorExpenditureBudgetPurposeData } =
    useExpenditureBudgetPurpose({
      id: project.data?.majorExpenditureBudgetPurposeId ?? '',
    });
  const { selectedRowKeys, setSelectedRowKeys } = useTableRowSelection();
  const { previewRowState, setPreviewRowState } = usePreviewRowState();
  const commentStackSiderView = useCommentStackSiderView();
  const enablePinpoint = commentStackSiderView.list.length > 0;

  const checkAccountTypeUsing = (
    accountingType: string,
    accountTitleId: string | null | undefined,
  ) => {
    if (!majorExpenditureBudgetPurposeData) {
      return true;
    }
    if (
      majorExpenditureBudgetPurposeData.accountingType.type !== accountingType
    ) {
      return false;
    }
    return majorExpenditureBudgetPurposeData.accountTitleId === accountTitleId;
  };

  const accountingData = useMemo(() => {
    if (
      previewRowState?.type === 'expenditure-cart' &&
      !!previewRowState.accountingType
    ) {
      return splitExpenditureCartsByAccountingType([
        ...data,
        {
          ...PREVIEW_ROW,
          accountingType: previewRowState.accountingType,
        },
      ]);
    }
    return splitExpenditureCartsByAccountingType(data);
  }, [data, previewRowState]);

  const expenditureCartIds = data.map((expenditureCart) => expenditureCart.id);

  const openAllExpanded = () => {
    addExpandedRowKeys(expenditureCartIds);
  };

  const closeAllExpanded = () => {
    removeExpandedRowKeys(expenditureCartIds);
  };

  useEffect(() => {
    if (
      previewRowState?.type === 'expenditure-cart' &&
      !!previewRowState.accountingType
    ) {
      tableRef.current?.scrollTo({
        index: data.length,
      });
    }
  }, [previewRowState, data]);

  const renderTable = (data: z.infer<typeof ExpenditureCartsModel>) => {
    return (
      <Table
        enableSticky={false}
        ref={tableRef}
        className={tableStyles}
        dataSource={data}
        rowKey="id"
        rowSelection={{
          renderCell: () => null,
          hideSelectAll: true,
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        columns={[
          {
            title: t('accounting-type'),
            key: 'accountingType',
            onHeaderCell: () => ({
              style: tableHeaderStyle,
            }),
            onCell: (record) => ({
              style: {
                minWidth:
                  record.id === PREVIEW_ROW.id
                    ? 200
                    : enablePinpoint
                      ? 180
                      : 80,
              },
            }),
            render: (_, record) => {
              if (record.id === PREVIEW_ROW.id) {
                return (
                  <Text
                    weight="regular"
                    color="secondary"
                    text={t('preview-row-description', {
                      item: t('accounting-name'),
                    })}
                  />
                );
              }

              return (
                <PinpointMarkerForTableCell
                  filteredOrganizationFiscalYear={project.data?.fiscalYear}
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: project.data
                      ?.skipAdministrativeReformTeamEvaluation
                      ? 'RS_SKIPPED_REVIEW'
                      : 'RS',
                    rs: {
                      path: {
                        project: id,
                        expenditureCart: record.id,
                      },
                      field: 'rs.budget-and-execution',
                    },
                    rsSkippedReview: {
                      path: {
                        project: id,
                        expenditureCart: record.id,
                      },
                      field: 'rs.skippedReview.budget-and-execution',
                    },
                  })}
                >
                  <Text
                    text={
                      AccountingTypeLabelModifier[record.accountingType.type]
                    }
                    weight="regular"
                  />
                </PinpointMarkerForTableCell>
              );
            },
          },
          {
            title: t('accounting-name'),
            key: 'counting',
            width: 240,
            onHeaderCell: () => ({
              style: tableHeaderStyle,
            }),
            render: (_, record) => {
              return record.id === PREVIEW_ROW.id
                ? ''
                : record.accountingType.name;
            },
          },
          {
            title: t('account-title'),
            key: 'accountTitle',
            onHeaderCell: () => ({
              style: tableHeaderStyle,
            }),
            onCell: () => ({
              style: { minWidth: 108 },
            }),
            render: (_, record) => record.accountTitle?.name,
          },
          {
            title: t('desired-amount'),
            key: 'desiredAmount',
            dataIndex: 'desiredAmount',
            align: 'right',
            width: 240,
            onHeaderCell: () => ({
              style: tableHeaderStyle,
            }),
            render: (value) =>
              formatValueByUnit(value, { operation: 'round', unit }),
          },
          {
            title: t('remark'),
            key: 'remark',
            dataIndex: 'remark',
            width: 350,
            onHeaderCell: () => ({
              style: tableHeaderStyle,
            }),
            render: (_, record) => {
              return <Text text={record.remark} maxRows={3} />;
            },
          },
          pinpointTableActionColumn<z.infer<typeof ExpenditureCartModel>>({
            showWithoutPinpoint: !!(
              project.data &&
              !project.data.isLocked &&
              editPermission.data
            ),
            render: (record) => ({
              pinpoint: {
                project: project.data,
                onPinpoint: () => {
                  commentStackSiderView.set({
                    payload: [
                      {
                        config: {
                          viewType: 'comment-form',
                          target: {
                            projectFiscalYear: project.data?.fiscalYear,
                          },
                          isEditing: false,
                          pinpoint: getPinpointSceneAndFieldBySheetType({
                            pinpointSheetType: project.data
                              ?.skipAdministrativeReformTeamEvaluation
                              ? 'RS_SKIPPED_REVIEW'
                              : 'RS',
                            rs: {
                              path: {
                                project: id,
                                expenditureCart: record.id,
                              },
                              field: 'rs.budget-and-execution',
                            },
                            rsSkippedReview: {
                              path: {
                                project: id,
                                expenditureCart: record.id,
                              },
                              field: 'rs.skippedReview.budget-and-execution',
                            },
                          }),
                        },
                        siderType: 'comment',
                      },
                    ],
                    target: {
                      type: 'project',
                      projectId,
                    },
                  });
                },
              },
              onEdit: () => {
                setDrawerState({
                  open: true,
                  type: 'expenditure-cart',
                  isEditing: true,
                  subProjectId,
                  expenditureCartId: record.id,
                });
                setSelectedRowKeys([record.id]);
              },
              items: [
                {
                  key: 'delete',
                  label: t('delete'),
                  danger: true,
                  onClick: () => {
                    onClickDelete(record);
                  },
                },
              ],
            }),
          }),
        ]}
        expandable={{
          expandedRowKeys,
          rowExpandable: (record) => record.id !== PREVIEW_ROW.id,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
          expandedRowRender: (expenditureCart) => {
            const executionItem = expenditureCart.executionItem;
            const isUseAccountType = checkAccountTypeUsing(
              expenditureCart.accountingType.type,
              expenditureCart.accountTitleId,
            );
            return (
              <Row gutter={[0, 16]}>
                <Col span={24}>
                  <ExpenditureItemsTable
                    data={expenditureCart.expenditureItems}
                    subProjectId={subProjectId}
                    expenditureCartId={expenditureCart.id}
                    isUseAccountType={isUseAccountType}
                    onClickDelete={(value) => {
                      setExpenditureItemDelete(value);
                    }}
                  />
                </Col>
                <Col span={24}>
                  <Row justify="end">
                    <Col style={{ marginRight: 24 }}>
                      <Text
                        weight="bold"
                        text={`[${t('execution-amount')}] ${formatValueByUnit(
                          executionItem?.amount,
                          {
                            operation: 'floor',
                            unit,
                          },
                        )}`}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            );
          },
        }}
        pagination={false}
      />
    );
  };

  return (
    <>
      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerBg: 'white',
              headerColor: '#00000073',
              rowExpandedBg: 'white',
              headerSplitColor: '#C0C0C0',
            },
          },
        }}
      >
        <Flex vertical gap={24}>
          <Flex justify="space-between">
            <Flex gap={16}>
              <PinpointMarker
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType: project.data
                    ?.skipAdministrativeReformTeamEvaluation
                    ? 'RS_SKIPPED_REVIEW'
                    : 'RS',
                  rs: {
                    path: { project: id },
                    field: 'rs.budget-and-execution-title',
                  },
                  rsSkippedReview: {
                    path: { project: id },
                    field: 'rs.skippedReview.budget-and-execution-title',
                  },
                })}
              >
                <Text variant="h3" text={t('budget-and-execution-title')} />
              </PinpointMarker>
              {data.length !== 0 && (
                <ToggleAllButton
                  isExpandedAllWhenMounted
                  openAll={openAllExpanded}
                  closeAll={closeAllExpanded}
                />
              )}
            </Flex>
            <ProjectResourceGuard onlyEdit>
              <PublishedProjectMutableGuard>
                {editable && (
                  <Space>
                    <Button
                      type="primary"
                      onClick={() => {
                        setDrawerState({
                          open: true,
                          type: 'expenditure-cart',
                          isEditing: false,
                          subProjectId,
                        });
                        setPreviewRowState({
                          type: 'expenditure-cart',
                          accountingType: undefined,
                        });
                        setSelectedRowKeys([PREVIEW_ROW_ID]);
                      }}
                    >
                      {t('add-expenditure-cart')}
                    </Button>
                  </Space>
                )}
              </PublishedProjectMutableGuard>
            </ProjectResourceGuard>
          </Flex>

          {data.length ? (
            <>{accountingData.map((v) => renderTable(v))}</>
          ) : (
            <Empty
              description={t('message-no-budget-and-execution-in-garage')}
            />
          )}
        </Flex>
      </ConfigProvider>

      <ConfirmModal
        open={expenditureItemDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: t('expenditure-item'),
        })}
        onOk={async () => {
          invariant(expenditureItemDelete, t('unreachable-code'));

          try {
            await deleteExpenditureItemAsync({
              id: expenditureItemDelete.id,
              projectId,
              subProjectId,
              expenditureCartId: expenditureItemDelete.expenditureCartId,
            });

            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setExpenditureItemDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => {
          setExpenditureItemDelete(null);
        }}
        skipCheck
      />
    </>
  );
};
